import { ErrorBoundary } from '@/components/error-boundary'
import { AdminLayout } from '@/components/layout/admin-layout'
import { LayoutSelector } from '@/components/layout/layout-selector'
import { MainLayout } from '@/components/layout/main-layout'
import { PortalLayout } from '@/components/layout/portal-layout'
import { ProtectedAdminRoute } from '@/components/layout/protected-admin-route'
import { ProtectedPortalRoute } from '@/components/layout/protected-portal-route'
import { ProtectedRoute } from '@/components/layout/protected-route'
import { ResponsiveLayout } from '@/components/layout/responsive-layout'
import { LenisProvider } from '@/components/lenis-provider'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useSessionState } from '@/hooks/use-session-state'
// SidebarLayout kept for reference but no longer used in embrace routes
// import { SidebarLayout } from '@/components/layout/sidebar-layout'
import {
  AdminAnalyticsPage,
  AdminBooksPage,
  AdminCrisisAlertsPage,
  AdminDLQPage,
  AdminDashboardPage,
  AdminImportPage,
  AdminOrganizationsPage,
  AdminPodcastsPage,
  AdminResourcesPage,
  AdminUserFormPage,
  AdminUsersPage,
} from '@/pages/admin'
import { ChatPage } from '@/pages/chat'
import { ConnectPage } from '@/pages/connect'
import { ContactPage } from '@/pages/contact'
// Dashboard redirects to /resume in new layout
// import { DashboardPage } from '@/pages/dashboard'
import { LandingPage } from '@/pages/landing'
import { LearnPage } from '@/pages/learn'
import { LearnBookPage } from '@/pages/learn-book'
import { LearnBooksPage } from '@/pages/learn-books'
import { LearnModulePage } from '@/pages/learn-module'
import { LearnOnboardPage } from '@/pages/learn-onboard'
import { LearnProfilePage } from '@/pages/learn-profile'
import { LearnResourcesPage } from '@/pages/learn-resources'
import { LearnStatsPage } from '@/pages/learn-stats'
import { LearnTopicPage } from '@/pages/learn-topic'
import { LoginPage } from '@/pages/login'
import { BloombergSearchingForWorkPage } from '@/pages/news/bloomberg-searching-for-work'
import { OpportunitiesPage } from '@/pages/opportunities'
import {
  PortalDashboardPage,
  PortalEmployeesPage,
  PortalImportHistoryPage,
  PortalImportPage,
  PortalLoginPage,
} from '@/pages/portal'
import { PrivacyPolicyPage } from '@/pages/privacy'
import { ResumePage } from '@/pages/resume'
import { SMSOptInExamplePage } from '@/pages/sms-opt-in-example'
import { StatusPage } from '@/pages/status'
import { TermsOfServicePage } from '@/pages/terms-of-service'
import { UpdatesPage } from '@/pages/updates'
import { WhitepaperLandingPage } from '@/pages/whitepaper'
import { WhitepaperDownloadPage } from '@/pages/whitepaper-download'
import { WhitepapersIndexPage } from '@/pages/whitepapers-index'
import { useAuthStore } from '@/stores/auth.store'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Loader } from 'lucide-react'
import { Navigate, RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom'
import type { SessionState } from './types'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

// VITE_SITE determines which site we're building for:
// - "pelgo" (pelgo.com): shows landing page at /
// - "embrace" (embrace.pelgo.com): redirects / to login/dashboard
// - "console" (console.pelgo.com): admin dashboard
const envSite: string = import.meta.env.VITE_SITE || 'embrace'
const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
const site: string = hostname === 'status.pelgo.com' ? 'status' : envSite

// Web: session-state API drives resume / opportunities. Mobile: /chat vs /opportunities from resume + prefs.
export function getRootRedirectTarget(
  isAuthenticated: boolean,
  sessionState: SessionState | null | undefined,
  hasSessionError: boolean,
  locationSearch: string,
): string {
  if (!isAuthenticated) {
    return `/login${locationSearch}`
  }

  if (hasSessionError || !sessionState) {
    return '/resume'
  }

  switch (sessionState.route) {
    case 'onboarding':
      return '/resume'
    case 'job_preferences':
      return '/resume'
    case 'opportunities':
      return '/opportunities'
  }
}

function RootRedirect() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isMobile = useIsMobile()
  const location = useLocation()
  const { data: sessionState, isLoading, isError } = useSessionState()

  if (!isAuthenticated) {
    return <Navigate to={`/login${location.search}`} replace />
  }

  if (!isMobile) {
    if (isLoading) {
      return null
    }
    const target = getRootRedirectTarget(isAuthenticated, sessionState, isError, location.search)
    return <Navigate to={target} replace />
  }

  return <MobileRedirect />
}

function MobileRedirect() {
  const { data: sessionState, isLoading } = useSessionState()

  if (isLoading)
    return (
      <div className="h-dvh flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-brand" />
      </div>
    )

  if (!sessionState || !sessionState.has_resume) {
    return <Navigate to="/chat" replace />
  }

  if (sessionState.has_resume && sessionState.has_preferences) {
    return <Navigate to="/opportunities" replace />
  }

  return <Navigate to="/chat" replace />
}

// Console (admin) routes
const consoleRouter = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <MainLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        element: <ProtectedAdminRoute />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                path: '/dashboard',
                element: <AdminDashboardPage />,
              },
              {
                path: '/users',
                element: <AdminUsersPage />,
              },
              {
                path: '/crisis-alerts',
                element: <AdminCrisisAlertsPage />,
              },
              {
                path: '/users/new',
                element: <AdminUserFormPage />,
              },
              {
                path: '/users/:id',
                element: <AdminUserFormPage />,
              },
              {
                path: '/import',
                element: <AdminImportPage />,
              },
              {
                path: '/analytics',
                element: <AdminAnalyticsPage />,
              },
              {
                path: '/organizations',
                element: <AdminOrganizationsPage />,
              },
              {
                path: '/podcasts',
                element: <AdminPodcastsPage />,
              },
              {
                path: '/books',
                element: <AdminBooksPage />,
              },
              {
                path: '/resources',
                element: <AdminResourcesPage />,
              },
              {
                path: '/dlq',
                element: <AdminDLQPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  // Legal pages
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <ErrorBoundary />,
  },
])

// Embrace (app) routes
const embraceRouter = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <ResponsiveLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/welcome',
            element: <Navigate to="/resume" state={{ fromWelcome: true }} replace />,
          }, // legacy URL; welcome UI lives on /login
          {
            element: <LayoutSelector />,
            children: [
              {
                path: '/dashboard',
                element: <Navigate to="/resume" replace />,
              },
              {
                path: '/chat',
                element: <ChatPage />,
              },
              {
                path: '/resume',
                element: <ResumePage />,
              },
              {
                path: '/connect',
                element: <ConnectPage />,
              },
              {
                path: '/opportunities',
                element: <OpportunitiesPage />,
              },
              {
                path: '/learn',
                element: <LearnPage />,
              },
              {
                path: '/learn/onboard',
                element: <LearnOnboardPage />,
              },
              {
                path: '/learn/profile',
                element: <LearnProfilePage />,
              },
              {
                path: '/learn/books',
                element: <LearnBooksPage />,
              },
              {
                path: '/learn/books/:bookId',
                element: <LearnBookPage />,
              },
              {
                path: '/learn/stats',
                element: <LearnStatsPage />,
              },
              {
                path: '/learn/resources',
                element: <LearnResourcesPage />,
              },
              {
                path: '/learn/:topicId',
                element: <LearnTopicPage />,
              },
              {
                path: '/learn/:topicId/:moduleId',
                element: <LearnModulePage />,
              },
            ],
          },
        ],
      },
    ],
  },
  // Legal and public pages without MainLayout header (show just "Pelgo")
  {
    path: '/sms-opt-in-example',
    element: <SMSOptInExamplePage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <ErrorBoundary />,
  },
])

// Pelgo (landing page) routes
const pelgoRouter = createBrowserRouter([
  {
    path: '/',
    element: <LandingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/status',
    element: <StatusPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/updates',
    element: <UpdatesPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/news/bloomberg-searching-for-work',
    element: <BloombergSearchingForWorkPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/resources/whitepapers',
    element: <WhitepapersIndexPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/resources/whitepapers/:slug',
    element: <WhitepaperLandingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/resources/whitepapers/:slug/download',
    element: <WhitepaperDownloadPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/assets/whitepapers/empathy-imperative-2026.pdf',
    element: <Navigate to="/resources/whitepapers/empathy-imperative-2026" replace />,
  },
  {
    path: '/assets/whitepapers/great-recomposition-2026.pdf',
    element: <Navigate to="/resources/whitepapers/great-recomposition-2026" replace />,
  },
])

const statusRouter = createBrowserRouter([
  {
    path: '/',
    element: <StatusPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicyPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfServicePage />,
    errorElement: <ErrorBoundary />,
  },
])

// Portal (HR admin) routes
const portalRouter = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/login',
    element: <PortalLoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    element: <ProtectedPortalRoute />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <PortalLayout />,
        children: [
          {
            path: '/dashboard',
            element: <PortalDashboardPage />,
          },
          {
            path: '/people',
            element: <PortalEmployeesPage />,
          },
          {
            path: '/import',
            element: <PortalImportPage />,
          },
          {
            path: '/imports',
            element: <PortalImportHistoryPage />,
          },
        ],
      },
    ],
  },
])

// Select router based on site
function getRouter() {
  switch (site) {
    case 'console':
      return consoleRouter
    case 'portal':
      return portalRouter
    case 'status':
      return statusRouter
    case 'pelgo':
      return pelgoRouter
    default:
      return embraceRouter
  }
}

function App() {
  const router = getRouter()
  const routerElement = <RouterProvider router={router} />

  return (
    <QueryClientProvider client={queryClient}>
      {/* Lenis for pelgo marketing routes only; embrace / console / portal use native scroll */}
      {site === 'pelgo' ? <LenisProvider>{routerElement}</LenisProvider> : routerElement}
      {import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}

export default App
